import Quill from 'quill'

const QuillModule = Quill.import('core/module')

const generateId = () => {
  const name = 'QUILL_ATTACHMENT'
  const id = new Date().getTime()
  return `${name}_${id}`
}

function ensureBusyStyle() {
  if (document.getElementById('global-busy-style')) return;
  const style = document.createElement('style');
  style.id = 'global-busy-style';
  style.textContent = `
    .busy, .busy * { cursor: wait !important; }
  `;
  document.head.appendChild(style);
}

class AttachmentModule extends QuillModule {
  // ... your fields

  constructor(quill: Quill, options: AttachmentOptionsInterface) {
    super(quill, options); // pass quill/options to super
    this.quill = quill;
    this.options = options;
    this.range = null;
    this.fileHolder = document.createElement('input');

    ensureBusyStyle();

    if (typeof this.options.upload !== 'function') {
      console.warn('[Missing config] upload(file) Promise<string> is required');
    }
    if (typeof this.options.render !== 'function') {
      console.warn('[Missing config] render(node, {file,url}) must return a DOM node');
    }

    this.quill.getModule('toolbar').addHandler('attachment', this.selectLocalImage.bind(this));
    this.quill.getModule('toolbar').addHandler('video', this.selectLocalVideo.bind(this));
  }

  selectLocalImage() {
    this.range = this.quill.getSelection();
    this.fileHolder = document.createElement('input');
    this.fileHolder.type = 'file';
    this.fileHolder.accept = '*/*';
    this.fileHolder.onchange = this.fileChanged.bind(this);
    this.fileHolder.click();
  }

  selectLocalVideo() {
    this.range = this.quill.getSelection();
    this.fileHolder = document.createElement('input');
    this.fileHolder.type = 'file';
    this.fileHolder.accept = 'video/*';
    this.fileHolder.onchange = this.videoChanged.bind(this);
    this.fileHolder.click();
  }

  videoChanged() {
    if (!this.fileHolder.files || this.fileHolder.files.length === 0) return;

    const file = this.fileHolder.files[0];
    document.documentElement.classList.add('busy');

    requestAnimationFrame(() => {
      this.options
        .upload(file)
        .then((url: string) => {
          const range = this.range || this.quill.getSelection(true) || { index: this.quill.getLength() };
          this.quill.insertEmbed(range.index, 'uploadedVideo', url, 'user');
        })
        .catch((error: any) => {
          console.warn(error?.message || error);
        })
        .finally(() => {
          document.documentElement.classList.remove('busy');
        });
    });
  }

  fileChanged() {
    if (!this.fileHolder.files || this.fileHolder.files.length === 0) return;

    const file = this.fileHolder.files[0];
    const attachmentId = generateId();

    document.documentElement.classList.add('busy');

    // 2) Defer to next frame so the cursor can repaint before the upload starts
    requestAnimationFrame(() => {
      // Now do the async work
      this.options
        .upload(file)
        .then((url: string) => {
          this.insertAttachment(file, attachmentId, url);
          const element = document.getElementById(attachmentId);
          if (typeof this.options.onFileUploaded === 'function' && element) {
            this.options.onFileUploaded(element, url);
          }
        })
        .catch((error: any) => {
          console.warn(error?.message || error);
        })
        .finally(() => {
          document.documentElement.classList.remove('busy');
        });
    });
  }

  insertAttachment(file: File, id: string, url: string) {
    // Defensive: ensure range exists
    const range = this.range || this.quill.getSelection(true) || { index: this.quill.getLength() };
    this.quill.insertEmbed(range.index, 'attachment', {
      id,
      properties: { file, url },
      render: this.options.render
    });
    // Do NOT revert cursor here anymore — handled in `.finally()`
  }
}


export interface AttachmentOptionsInterface {
  upload(file: File): Promise<string>
  render(node: HTMLElement, filetype: { file: File, url: string }): HTMLElement
  onFileUploaded(elem: HTMLElement, url: string): void
}

export default AttachmentModule
